import { Component, EventEmitter, NgZone, Output } from '@angular/core';
import { buttonInOut } from 'animations-lib';
import { Observable } from 'rxjs';
import { DOCUMENT_SENDER_ENTITY_LABEL_PAIR, EntityTypeLabelPair, IFieldMeta } from '../../../interfaces/imetadata';
import { FieldConfig } from '../../../utils/fieldconfig';
import { FieldType } from '../../../utils/fieldtype';
import { FilterableItem, IFilterableItem } from '../../filterable-list/filterable-item';
import { FieldConfigChangedControllerService } from '../data-fields/field-config-changed-controller.service';
import { IValidStateChangeProvider, ValidStateProvider } from '../valid-state-provider';
import { DocumentSenderFieldProviderService, FieldAndConfig, ValidDocumentSenderFields } from './document-sender-field-provider.service';

@Component({
    selector: 'app-document-sender',
    templateUrl: './document-sender.component.html',
    styleUrls: ['../field-selection.component.scss', '../field-config/field-config.component.scss'],
    animations: [buttonInOut],
    standalone: false
})
export class DocumentSenderComponent implements IValidStateChangeProvider {
  @Output()
  fieldClickedEvent = new EventEmitter<{
    entity: EntityTypeLabelPair;
    field: IFieldMeta;
  }>();

  @Output()
  fieldUpdatedEvent = new EventEmitter<{
    entity: EntityTypeLabelPair;
    field: IFieldMeta;
    fieldConfig: FieldConfig;
  }>();

  fields: IFilterableItem[] = [];
  activeField: IFieldMeta | undefined = undefined;

  message: string | undefined = undefined;
  protected modified = false;

  // config options
  fieldIsConfigurable = false;
  fieldConfig: FieldConfig = {};
  optionsOpen = false;

  private valid = new ValidStateProvider();

  private static NO_INSERT_MESSAGE = 'Document sender fields cannot be inserted at the current location.';
  private static NO_CONFIG_MESSAGE = 'There are no configurable options available for this field.';

  constructor(
    documentSenderFieldProviderService: DocumentSenderFieldProviderService,
    private configChangedControllerService: FieldConfigChangedControllerService,
    ngZone: NgZone
  ) {
    documentSenderFieldProviderService.validFieldsChanged$.subscribe({
      next: (data) => {
        ngZone.run(() => this.setFields(data));
      },
    });

    this.configChangedControllerService.optionsChanged$.subscribe((changed) => {
      this.modified = changed;
    });
  }

  get validStateChange$(): Observable<boolean> {
    return this.valid.validStateChange$;
  }

  onFieldSelected(item: IFilterableItem): void {
    this.fieldClickedEvent.emit({
      entity: DOCUMENT_SENDER_ENTITY_LABEL_PAIR,
      field: item.data as IFieldMeta,
    });
  }

  openConfig(event: Event): void {
    this.optionsOpen = true;
    event.stopPropagation();
  }

  closeOptions(): void {
    this.optionsOpen = false;
  }

  onUpdateClick() {
    if (this.activeField) {
      this.fieldUpdatedEvent.emit({
        entity: DOCUMENT_SENDER_ENTITY_LABEL_PAIR,
        field: this.activeField,
        fieldConfig: this.fieldConfig,
      });
      this.closeOptions();
    }
  }

  private setFields(data: ValidDocumentSenderFields | undefined): void {
    if (!data) {
      this.fields = [];
      this.activeField = undefined;
      this.optionsOpen = false;
      this.message = DocumentSenderComponent.NO_INSERT_MESSAGE;
      this.valid.setValid(false);
      return;
    }

    if (data.fields instanceof FieldAndConfig) {
      this.fields = [];
      this.activeField = data.fields.field;
      this.fieldConfig = data.fields.config;
      this.fieldIsConfigurable = this.getFieldConfigurable(this.activeField.type);
      this.optionsOpen = this.fieldIsConfigurable;
      this.message = this.fieldIsConfigurable ? undefined : DocumentSenderComponent.NO_CONFIG_MESSAGE;
    } else {
      this.activeField = undefined;
      this.fieldConfig = {}; // why?
      this.fieldIsConfigurable = false;
      this.optionsOpen = false;
      this.fields = data.fields.map((f) => FilterableItem.fromFieldMeta(f));
      this.message = this.fields.length > 0 ? undefined : DocumentSenderComponent.NO_INSERT_MESSAGE;
    }

    this.valid.setValid(true);
  }

  private getFieldConfigurable(fieldType: string | undefined): boolean {
    return (
      fieldType === FieldType.DOUBLE || fieldType === FieldType.INTEGER || fieldType === FieldType.DATE || fieldType === FieldType.TO_MANY
    );
  }
}
